import { useState, useEffect } from "react";
import axios from "axios";
import { API } from "../../lib/api";
import { FilePdf, ArrowClockwise, XCircle, CreditCard, CheckCircle } from "@phosphor-icons/react";

const fmtMXN = (v) => (v || 0).toLocaleString("es-MX", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const DOC_LABELS = {
  nota_venta: "PREFACTURA",
  remision: "PREFACTURA",
  prefactura: "PREFACTURA",
  factura: "PREFACTURA",
};

export default function POSPublicView({ publicToken }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [payingMP, setPayingMP] = useState(false);
  const [paymentResult, setPaymentResult] = useState(null);

  useEffect(() => {
    axios.get(`${API}/pos/sales/public/${publicToken}`)
      .then(res => setData(res.data))
      .catch(e => setError(e.response?.status === 404 ? "Documento no encontrado" : "Error al cargar"))
      .finally(() => setLoading(false));

    // Check URL params for payment result and sync with backend
    const params = new URLSearchParams(window.location.search);
    const payStatus = params.get("payment");
    if (payStatus) {
      setPaymentResult(payStatus);
      // If success, poll backend to sync MP payment status
      if (payStatus === "success") {
        setTimeout(() => {
          axios.get(`${API}/pos/sales/public/${publicToken}`).then(res => setData(res.data)).catch(() => {});
        }, 2000);
      }
    }
  }, [publicToken]);

  const handlePdf = async () => {
    try {
      const res = await axios.get(`${API}/pos/sales/public/${publicToken}/pdf`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = `${sale.folio || "documento"}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch { alert("Error al descargar PDF"); }
  };

  const handlePayMP = async () => {
    setPayingMP(true);
    try {
      const res = await axios.post(`${API}/payments/mercadopago/create/${sale.id}`);
      const { init_point } = res.data;
      if (init_point) {
        window.location.assign(init_point);
      } else {
        alert("No se pudo generar el link de pago");
        setPayingMP(false);
      }
    } catch (e) {
      alert(e.response?.data?.detail || "Error al crear pago");
      setPayingMP(false);
    }
  };

  if (loading) return (
    <div className="min-h-screen flex items-center justify-center bg-white">
      <ArrowClockwise size={40} className="animate-spin text-blue-500" />
    </div>
  );

  if (error) return (
    <div className="min-h-screen flex items-center justify-center bg-white">
      <div className="text-center">
        <XCircle size={48} className="mx-auto text-red-400 mb-3" />
        <p className="text-lg text-slate-600">{error}</p>
      </div>
    </div>
  );

  const sale = data.sale;
  const cfg = data.config || {};
  const pmLabels = data.payment_method_labels || {};

  // Card-only amount for MP button
  const cardMethods = ["tarjeta", "tarjeta_credito", "tarjeta_debito"];
  const cardAmount = (sale.payments || []).filter(p => cardMethods.includes(p.method)).reduce((s, p) => s + (p.amount || 0), 0);
  const mpDisplayAmount = sale.mp_amount || (cardAmount > 0 ? cardAmount : sale.total) || 0;

  const docLabel = DOC_LABELS[sale.doc_type] || "DOCUMENTO";
  const tc = cfg.template_colors || {};
  const primaryColor = tc.primary || "#0F172A";
  const accentColor = tc.accent || "#2563EB";
  const isReturned = sale.status === "devuelta" || sale.status === "parcial_devuelta";

  return (
    <div className="min-h-screen bg-slate-100 py-6 px-4" style={{ fontFamily: "'Helvetica Neue', Arial, sans-serif" }} data-testid="pos-public-view">
      <div className="max-w-[850px] mx-auto bg-white shadow-lg">

        {/* ===== TOP COLOR BAR ===== */}
        <div style={{ backgroundColor: isReturned ? "#dc2626" : primaryColor, height: "6px" }} />

        {/* Return banner */}
        {isReturned && (
          <div className="bg-red-600 text-white text-center py-3 px-4">
            <p className="text-sm font-bold uppercase tracking-wider">
              {sale.status === "devuelta" ? "DOCUMENTO DEVUELTO / CANCELADO" : "DEVOLUCION PARCIAL"}
            </p>
            <p className="text-xs text-red-200">Este documento ha sido {sale.status === "devuelta" ? "devuelto en su totalidad" : "parcialmente devuelto"}</p>
          </div>
        )}

        {/* ===== HEADER: Logo + Company + Doc Type ===== */}
        <div className="px-8 pt-6 pb-4">
          <div className="flex items-start justify-between gap-4">
            {/* LEFT: Logo + Company */}
            <div className="flex items-start gap-4">
              {cfg.logo_url && (
                <img src={cfg.logo_url} alt="Logo" className="w-20 h-20 object-contain" data-testid="pos-public-logo" />
              )}
              <div>
                <h1 className="text-lg font-bold" style={{ color: primaryColor }}>{cfg.company_name || "Empresa"}</h1>
                {cfg.rfc && <p className="text-xs text-gray-600">RFC Emisor: {cfg.rfc}</p>}
                {cfg.phone && <p className="text-xs text-gray-600">Tel.: {cfg.phone}</p>}
                {cfg.address && <p className="text-xs text-gray-600">{cfg.address} {cfg.postal_code ? `C.P ${cfg.postal_code}` : ""}</p>}
                {cfg.tax_regime && <p className="text-xs text-gray-600">Regimen Fiscal: {cfg.tax_regime}</p>}
              </div>
            </div>
            {/* RIGHT: Doc type + number */}
            <div className="text-right shrink-0">
              <div className="inline-block px-5 py-2 text-white font-bold text-base" style={{ backgroundColor: primaryColor }}>
                {docLabel}
              </div>
              <p className="font-bold text-sm mt-2" style={{ color: primaryColor }}>NUMERO: {sale.folio}</p>
            </div>
          </div>
        </div>

        {/* ===== SEPARATOR ===== */}
        <div className="mx-8" style={{ borderBottom: `2px solid ${accentColor}` }} />

        {/* ===== DETAILS: Receptor + Dates ===== */}
        <div className="px-8 py-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-xs">
            {/* Receptor */}
            <div className="border p-3" style={{ borderColor: accentColor }}>
              <p className="font-bold text-xs mb-1" style={{ color: primaryColor }}>Receptor</p>
              <p className="font-semibold text-gray-800">{sale.customer_name || "Publico General"}</p>
              {sale.customer_rfc && <p className="text-gray-600">RFC: {sale.customer_rfc}</p>}
            </div>
            {/* Fecha y vendedor */}
            <div className="border p-3" style={{ borderColor: accentColor }}>
              <p className="font-bold text-xs mb-1" style={{ color: primaryColor }}>Datos del Documento</p>
              <p className="text-gray-600">Fecha: <span className="text-gray-800 font-medium">{(sale.created_at || "").slice(0, 10)}</span></p>
              <p className="text-gray-600">Vendedor: <span className="text-gray-800 font-medium">{sale.created_by_name || "-"}</span></p>
              <p className="text-gray-600">Sucursal: <span className="text-gray-800 font-medium">{sale.branch_id || "Matriz"}</span></p>
            </div>
            {/* Folio */}
            <div className="border p-3" style={{ borderColor: accentColor }}>
              <p className="font-bold text-xs mb-1" style={{ color: primaryColor }}>Referencia</p>
              <p className="text-gray-600">Folio: <span className="text-gray-800 font-bold">{sale.folio}</span></p>
              <p className="text-gray-600">Tipo: <span className="text-gray-800 font-medium">{docLabel}</span></p>
              <p className="text-gray-600">Estatus: <span className="text-gray-800 font-medium">{sale.status === "completada" ? "Completada" : "Pendiente"}</span></p>
            </div>
          </div>
        </div>

        {/* ===== ITEMS TABLE ===== */}
        <div className="px-8 pb-4">
          <table className="w-full text-xs border-collapse" data-testid="pos-public-items-table">
            <thead>
              <tr style={{ backgroundColor: primaryColor }}>
                <th className="px-3 py-2 text-left text-white font-semibold border-r border-white/20">Codigo</th>
                <th className="px-3 py-2 text-left text-white font-semibold border-r border-white/20">Producto / Descripcion</th>
                <th className="px-3 py-2 text-center text-white font-semibold border-r border-white/20 w-16">Cant.</th>
                <th className="px-3 py-2 text-right text-white font-semibold border-r border-white/20 w-24">P. Unitario</th>
                <th className="px-3 py-2 text-right text-white font-semibold w-24">Importe</th>
              </tr>
            </thead>
            <tbody>
              {(sale.items || []).map((item, i) => (
                <tr key={`item-${i}`} className="border-b border-gray-200">
                  <td className="px-3 py-2 text-gray-600 font-mono border-r border-gray-200">{item.sku || "-"}</td>
                  <td className="px-3 py-2 text-gray-800 font-medium border-r border-gray-200">
                    {item.name}
                    {item.discount_pct > 0 && <span className="ml-2 text-red-600 text-[10px]">(-{item.discount_pct}%)</span>}
                  </td>
                  <td className="px-3 py-2 text-center text-gray-800 border-r border-gray-200">{item.quantity}</td>
                  <td className="px-3 py-2 text-right text-gray-800 font-mono border-r border-gray-200">${fmtMXN(item.unit_price)}</td>
                  <td className="px-3 py-2 text-right text-gray-900 font-mono font-semibold">${fmtMXN(item.line_total)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* ===== BOTTOM: Payment info (left) + Totals (right) ===== */}
        <div className="px-8 pb-4">
          <div className="grid grid-cols-1 md:grid-cols-[1fr_280px] gap-4">
            {/* LEFT: Payment details + importe con letra */}
            <div className="border p-3 text-xs" style={{ borderColor: accentColor }}>
              <p className="font-bold mb-2" style={{ color: primaryColor }}>Informacion de Pago</p>
              {(sale.payments || []).map((p, i) => (
                <p key={`pay-${i}`} className="text-gray-600 mb-0.5">
                  Forma de pago: <span className="text-gray-800 font-medium">{pmLabels[p.method] || p.method} - ${fmtMXN(p.amount)}</span>
                </p>
              ))}
              {sale.change_amount > 0 && (
                <p className="text-gray-600">Cambio: <span className="text-green-700 font-medium">${fmtMXN(sale.change_amount)}</span></p>
              )}
              {sale.notes && (
                <p className="text-gray-600 mt-2">Notas: <span className="text-gray-800">{sale.notes}</span></p>
              )}
              <div className="mt-3 pt-2 border-t border-gray-200">
                <p className="text-gray-600"><span className="font-semibold text-gray-800">Importe con letra:</span></p>
                <p className="text-gray-800 font-medium uppercase text-[11px]">
                  {numberToSpanishWords(Math.floor(sale.total || 0))} PESOS {String(Math.round(((sale.total || 0) - Math.floor(sale.total || 0)) * 100)).padStart(2, '0')}/100 M.N.
                </p>
              </div>
            </div>

            {/* RIGHT: Totals box */}
            <div className="border text-xs" style={{ borderColor: accentColor }}>
              <div className="p-3 space-y-1.5">
                <div className="flex justify-between">
                  <span className="text-gray-600">Subtotal:</span>
                  <span className="font-mono font-medium text-gray-800">${fmtMXN(sale.subtotal)}</span>
                </div>
                {sale.global_discount_amount > 0 && (
                  <div className="flex justify-between">
                    <span className="text-gray-600">Descuento:</span>
                    <span className="font-mono font-medium text-red-600">-${fmtMXN(sale.global_discount_amount)}</span>
                  </div>
                )}
                <div className="flex justify-between">
                  <span className="text-gray-600">I.V.A.:</span>
                  <span className="font-mono font-medium text-gray-800">${fmtMXN(sale.tax_total)}</span>
                </div>
              </div>
              <div className="flex justify-between p-3 font-bold text-sm" style={{ backgroundColor: primaryColor, color: "white" }} data-testid="pos-public-total">
                <span>Total:</span>
                <span className="font-mono">${fmtMXN(sale.total)} MXN</span>
              </div>
            </div>
          </div>
        </div>

        {/* ===== SEPARATOR ===== */}
        <div className="mx-8" style={{ borderBottom: `1px solid ${accentColor}` }} />

        {/* ===== CONFIGURABLE TEXTS (Conditions, Warranty, Terms) ===== */}
        <div className="px-8 py-4 space-y-3 text-xs text-gray-700">
          {cfg.include_payment_terms && cfg.payment_terms && (
            <div>
              <p className="font-bold mb-0.5" style={{ color: primaryColor }}>Condiciones de Pago</p>
              <p className="leading-relaxed">{cfg.payment_terms}</p>
            </div>
          )}
          {cfg.include_sale_notes && cfg.sale_notes && (
            <div>
              <p className="font-bold mb-0.5" style={{ color: primaryColor }}>Notas</p>
              <p className="leading-relaxed">{cfg.sale_notes}</p>
            </div>
          )}
          {cfg.include_warranty_notes && cfg.warranty_notes && (
            <div>
              <p className="font-bold mb-0.5" style={{ color: primaryColor }}>Garantia</p>
              <p className="leading-relaxed">{cfg.warranty_notes}</p>
            </div>
          )}
          {cfg.include_terms_conditions && cfg.terms_conditions && (
            <div>
              <p className="font-bold mb-0.5" style={{ color: primaryColor }}>Terminos y Condiciones</p>
              <p className="leading-relaxed">{cfg.terms_conditions}</p>
            </div>
          )}
        </div>

        {/* ===== PAYMENT STATUS BANNER ===== */}
        {paymentResult === "success" && (
          <div className="mx-8 my-4 p-4 bg-emerald-50 border border-emerald-200 rounded-lg flex items-center gap-3">
            <CheckCircle size={24} className="text-emerald-600 shrink-0" />
            <div>
              <p className="text-sm font-bold text-emerald-800">Pago recibido exitosamente</p>
              <p className="text-xs text-emerald-600">Tu pago fue procesado correctamente. Gracias por tu compra.</p>
            </div>
          </div>
        )}
        {paymentResult === "pending" && (
          <div className="mx-8 my-4 p-4 bg-amber-50 border border-amber-200 rounded-lg flex items-center gap-3">
            <ArrowClockwise size={24} className="text-amber-600 shrink-0" />
            <div>
              <p className="text-sm font-bold text-amber-800">Pago pendiente</p>
              <p className="text-xs text-amber-600">Tu pago esta siendo procesado. Te notificaremos cuando se confirme.</p>
            </div>
          </div>
        )}
        {paymentResult === "failure" && (
          <div className="mx-8 my-4 p-4 bg-red-50 border border-red-200 rounded-lg flex items-center gap-3">
            <XCircle size={24} className="text-red-600 shrink-0" />
            <div>
              <p className="text-sm font-bold text-red-800">Pago no completado</p>
              <p className="text-xs text-red-600">El pago no pudo procesarse. Puedes intentar nuevamente.</p>
            </div>
          </div>
        )}

        {/* ===== PAYMENT BUTTON (only for pending sales) ===== */}
        {sale.status === "pendiente" && !isReturned && !paymentResult && (
          <div className="px-8 py-4">
            <button
              onClick={handlePayMP}
              disabled={payingMP}
              className="w-full py-3.5 rounded-lg text-white font-bold text-sm flex items-center justify-center gap-2 transition-all hover:opacity-90 disabled:opacity-50"
              style={{ backgroundColor: "#009ee3" }}
              data-testid="pos-public-pay-mp"
            >
              <CreditCard size={20} weight="bold" />
              {payingMP ? "Redirigiendo a Mercado Pago..." : `Pagar $${fmtMXN(mpDisplayAmount)} con Mercado Pago`}
            </button>
            <p className="text-center text-[10px] text-gray-400 mt-2">Paga con tarjeta de credito, debito o tu cuenta de Mercado Pago</p>
          </div>
        )}

        {sale.status === "completada" && sale.paid_via === "mercadopago" && (
          <div className="mx-8 my-4 p-4 bg-emerald-50 border border-emerald-200 rounded-lg flex items-center gap-3">
            <CheckCircle size={24} className="text-emerald-600 shrink-0" />
            <div>
              <p className="text-sm font-bold text-emerald-800">Pagado con Mercado Pago</p>
              <p className="text-xs text-emerald-600">Este documento fue pagado en linea.</p>
            </div>
          </div>
        )}

        {/* ===== FOOTER ===== */}
        <div className="px-8 py-4 border-t border-gray-200 flex items-center justify-between">
          <button onClick={handlePdf} className="flex items-center gap-2 text-xs font-medium hover:opacity-80" style={{ color: accentColor }} data-testid="pos-public-pdf-btn">
            <FilePdf size={18} /> Descargar PDF
          </button>
          <p className="text-[10px] text-gray-400">
            Documento generado por {cfg.company_name || "MaxTicket POS"}
          </p>
        </div>

        {/* BOTTOM BAR */}
        <div style={{ backgroundColor: primaryColor, height: "4px" }} />
      </div>
    </div>
  );
}

function numberToSpanishWords(n) {
  if (n === 0) return "CERO";
  const units = ["", "UN", "DOS", "TRES", "CUATRO", "CINCO", "SEIS", "SIETE", "OCHO", "NUEVE"];
  const teens = ["DIEZ", "ONCE", "DOCE", "TRECE", "CATORCE", "QUINCE", "DIECISEIS", "DIECISIETE", "DIECIOCHO", "DIECINUEVE"];
  const tens = ["", "", "VEINTE", "TREINTA", "CUARENTA", "CINCUENTA", "SESENTA", "SETENTA", "OCHENTA", "NOVENTA"];
  const hundreds = ["", "CIENTO", "DOSCIENTOS", "TRESCIENTOS", "CUATROCIENTOS", "QUINIENTOS", "SEISCIENTOS", "SETECIENTOS", "OCHOCIENTOS", "NOVECIENTOS"];

  function belowThousand(num) {
    if (num === 0) return "";
    if (num === 100) return "CIEN";
    let r = "";
    if (num >= 100) { r += hundreds[Math.floor(num / 100)] + " "; num %= 100; }
    if (num >= 20) { r += tens[Math.floor(num / 10)]; if (num % 10) r += " Y " + units[num % 10]; num = 0; }
    else if (num >= 10) { r += teens[num - 10]; num = 0; }
    if (num > 0) r += units[num];
    return r.trim();
  }

  if (n >= 1000000) {
    const m = Math.floor(n / 1000000);
    const rest = n % 1000000;
    const prefix = m === 1 ? "UN MILLON" : `${belowThousand(m)} MILLONES`;
    return rest ? `${prefix} ${numberToSpanishWords(rest)}` : prefix;
  }
  if (n >= 1000) {
    const t = Math.floor(n / 1000);
    const rest = n % 1000;
    const prefix = t === 1 ? "MIL" : `${belowThousand(t)} MIL`;
    return rest ? `${prefix} ${belowThousand(rest)}` : prefix;
  }
  return belowThousand(n);
}
